import { Box, Button, Chip, Typography, useTheme } from "@mui/material";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ReactPlayer from "react-player";
import { FormattedMessage } from "react-intl";
import { resolveMediaUrl } from "../../helper/resolveMediaUrl";

function formatDate(value, isAr) {
    if (!value) return isAr ? "غير متوفر" : "Unavailable";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat(isAr ? "ar-EG" : "en-US", { dateStyle: "medium" }).format(parsed);
}

export default function AdminCourseFilePreview({
    contents = [],
    language = "ar",
    emptyHintAr = "لا توجد ملفات مرفوعة.",
    emptyHintEn = "No uploaded files were found.",
}) {
    const theme = useTheme();
    const isAr = language === "ar";
    const files = contents.filter((content) => content.content_type === "CourseFile");

    if (!files.length) {
        return (
            <Box
                sx={{
                    p: 4,
                    borderRadius: 3,
                    border: "1px dashed",
                    borderColor: theme.palette.mode === "dark" ? "divider" : "#cbd5e1",
                    textAlign: "center",
                    color: "text.secondary",
                }}
            >
                {isAr ? emptyHintAr : emptyHintEn}
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {files.map((content) => {
                const item = content.content || {};
                const title = isAr ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar);
                const mediaUrl = resolveMediaUrl(item.file_url);
                const isVideo = Boolean(item.is_video);

                return (
                    <Box
                        key={content.id || item.id}
                        sx={{
                            border: "1px solid",
                            borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                            borderRadius: 3,
                            overflow: "hidden",
                            bgcolor: theme.palette.mode === "dark" ? "background.default" : "#fff",
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                pt: isVideo && mediaUrl ? "56.25%" : undefined,
                                minHeight: isVideo && mediaUrl ? undefined : 220,
                                bgcolor: isVideo && mediaUrl ? "#000" : (theme.palette.mode === "dark" ? "background.paper" : "#f8fafc"),
                            }}
                        >
                            {isVideo && mediaUrl ? (
                                <Box sx={{ position: "absolute", inset: 0 }}>
                                    <ReactPlayer src={mediaUrl} controls width="100%" height="100%" />
                                </Box>
                            ) : mediaUrl ? (
                                <Box
                                    sx={{
                                        height: "100%",
                                        minHeight: 220,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1.5,
                                        p: 3,
                                        textAlign: "center",
                                    }}
                                >
                                    <PictureAsPdfOutlinedIcon sx={{ fontSize: 54, color: "#dc2626" }} />
                                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#0D358C" }}>
                                        {isAr ? "ملف PDF للجلسة" : "Lesson PDF file"}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<OpenInNewOutlinedIcon />}
                                        href={mediaUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {isAr ? "فتح الملف" : "Open file"}
                                    </Button>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        height: "100%",
                                        minHeight: 220,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "text.secondary",
                                    }}
                                >
                                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 48 }} />
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ p: 2 }}>
                            <Typography fontWeight={800} sx={{ mb: 1 }}>
                                {title || (isAr ? "ملف بدون اسم" : "Untitled file")}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                                <Chip
                                    size="small"
                                    icon={isVideo ? <OndemandVideoOutlinedIcon /> : <PictureAsPdfOutlinedIcon />}
                                    label={isVideo ? <FormattedMessage id="video" /> : (isAr ? "ملف" : "File")}
                                    variant="outlined"
                                />
                                <Chip
                                    size="small"
                                    icon={<CalendarMonthOutlinedIcon />}
                                    label={formatDate(item.created_at, isAr)}
                                    variant="outlined"
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {isAr
                                    ? "يمكنك معاينة هذا المحتوى مباشرة قبل اتخاذ أي قرار."
                                    : "You can preview this content directly before taking any action."}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}
